import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Spinner, Table } from 'react-bootstrap';
import Wrapper from '../../components/Wrapper';
import CollapsibleSection from '../../components/CollapsibleSection';
import { deviceApi } from '../../services/deviceApi';

const diffStatusBadge = (status) => {
  const map = {
    ADDED: 'success',
    REMOVED: 'danger',
    MODIFIED: 'warning',
    UNCHANGED: 'secondary',
  };
  return <Badge bg={map[status] || 'secondary'}>{status}</Badge>;
};

const DeviceHardwareVersion = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [diffs, setDiffs] = useState({});
  const [diffLoading, setDiffLoading] = useState({});
  const loadedDiffKeys = useRef(new Set());

  useEffect(() => {
    deviceApi
      .getHardwareDropdown('device')
      .then(setVersions)
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load hardware versions.'))
      .finally(() => setLoading(false));
  }, []);

  const loadDiff = (eventKey, targetId, baseId) => {
    if (!targetId || !baseId || loadedDiffKeys.current.has(eventKey)) return;

    loadedDiffKeys.current.add(eventKey);
    setDiffLoading((prev) => ({ ...prev, [eventKey]: true }));

    deviceApi
      .getHardwareDiff(targetId, baseId)
      .then((diff) => {
        setDiffs((prev) => ({ ...prev, [eventKey]: diff }));
      })
      .catch(() => {
        setDiffs((prev) => ({ ...prev, [eventKey]: [] }));
      })
      .finally(() => {
        setDiffLoading((prev) => ({ ...prev, [eventKey]: false }));
      });
  };

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-3 w-100">
          <h1 className="fw-bolder">Hardware Version</h1>
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && versions.length === 0 && (
          <Alert variant="info">No device hardware versions found.</Alert>
        )}

        {!loading && versions.length > 0 && versions.map((version, index) => {
          const older = versions[index + 1];
          const eventKey = String(version.id);
          const diff = diffs[eventKey];
          const title = (
            <>
              <span className="text-uppercase">{version.version_tag}</span>
              {index === 0 && <Badge bg="success" className="ms-2">Latest</Badge>}
            </>
          );

          return (
            <CollapsibleSection
              key={version.id}
              title={title}
              onToggle={(isOpen) => {
                if (isOpen && older) {
                  loadDiff(eventKey, version.id, older.id);
                }
              }}
            >
              <p className="text-muted mb-3">
                Hardware profile: <strong className="text-capitalize">{version.name}</strong>
              </p>

              {!older ? (
                <p className="text-muted mb-0">This is the earliest recorded hardware version.</p>
              ) : (
                <>
                  <p className="mb-3">
                    Changes from <Badge bg="light" text="dark">{older.version_tag}</Badge>
                    {' '}to <Badge bg="primary">{version.version_tag}</Badge>
                  </p>

                  {diffLoading[eventKey] && (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Spinner animation="border" size="sm" />
                      <span>Loading BOM changes...</span>
                    </div>
                  )}

                  {!diffLoading[eventKey] && diff && diff.length === 0 && (
                    <p className="text-muted mb-0">No BOM component changes between these versions.</p>
                  )}

                  {!diffLoading[eventKey] && diff && diff.length > 0 && (
                    <Table striped bordered size="sm" className="mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th>Component</th>
                          <th>Old Qty</th>
                          <th>New Qty</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diff.map((row) => (
                          <tr key={`${row.component_id}-${row.status}`}>
                            <td>{row.name}</td>
                            <td>{row.old_quantity} {row.unit}</td>
                            <td>{row.new_quantity} {row.unit}</td>
                            <td>{diffStatusBadge(row.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                </>
              )}
            </CollapsibleSection>
          );
        })}
      </div>
    </Wrapper>
  );
};

export default DeviceHardwareVersion;
